import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { isInNetwork, NETWORK_KEY_MAPPINGS_BY_VENDOR } from '../../../helpers/formattedDentalCoverageTableData';
import { SegregatedDataByNetworkType, TablesDataType, Vendors } from '../types/types';
import { filterTableDataBySearchTermAndNetworkType, segregateByNetwork } from '../utils/utils';

interface UseNetworkTypeFilterTableDataProps {
  allTablesData: TablesDataType;
  isLoading: boolean;
  vendor: Vendors;
}

export interface UpdateTablesDataParamsType {
  searchTerm?: string;
  filterType?: string;
}

export interface NetworkTypeFilterSwitchProps {
  switchValue: string;
  setSwitchValue: Dispatch<SetStateAction<string>>;
}

export interface UseNetworkTypeFilterTableDataReturnType {
  filteredTablesData: TablesDataType;
  segregatedDataByNetwork: SegregatedDataByNetworkType; //This contains both in-network and out-network data which will be used in showing report
  updateTablesData: (params: UpdateTablesDataParamsType) => void;
}

export const useNetworkTypeFilterData = ({
  allTablesData,
  isLoading,
  vendor,
}: UseNetworkTypeFilterTableDataProps): UseNetworkTypeFilterTableDataReturnType => {
  const [filteredData, setFilteredData] = useState<TablesDataType>({});

  const [filterChanged, setFilterChanged] = useState(NETWORK_KEY_MAPPINGS_BY_VENDOR[vendor]['in-network']);

  const [searchTerm, setSearchTerm] = useState<string>('');

  const [segregatedDataByNetwork, setSegregatedDataByNetwork] = useState<SegregatedDataByNetworkType>({
    in_network: {} as TablesDataType,
    out_network: {} as TablesDataType,
  });

  useEffect(() => {
    if (!isLoading) {
      setSegregatedDataByNetwork(segregateByNetwork(allTablesData));
    }
  }, [allTablesData, isLoading]);

  useEffect(() => {
    const currentFilterData = isInNetwork(filterChanged, vendor)
      ? segregatedDataByNetwork.in_network
      : segregatedDataByNetwork.out_network;

    if (searchTerm === '') {
      setFilteredData(currentFilterData);
    } else {
      const matchedSearchTermDataAndNetworkType = filterTableDataBySearchTermAndNetworkType(
        searchTerm,
        currentFilterData
      );
      setFilteredData(matchedSearchTermDataAndNetworkType);
    }
  }, [segregatedDataByNetwork, searchTerm, filterChanged]);

  const updateTablesData = useCallback((paramas: UpdateTablesDataParamsType) => {
    if (paramas.filterType) {
      setFilterChanged(paramas.filterType);
    }
    if (paramas.searchTerm || paramas.searchTerm === '') {
      setSearchTerm(paramas.searchTerm);
    }
  }, []);

  return {
    filteredTablesData: filteredData,
    segregatedDataByNetwork,
    updateTablesData,
  };
};
